import React, { useEffect, useRef, useState } from 'react'
import './experience.css'

const Experience = () => {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const itemRefs = useRef([]);

  const education = {
    institution: "University of Wisconsin-Milwaukee",
    degree: "Bachelor of Science in Computer Science",
    gpa: "3.7/4.00",
    location: "Milwaukee, WI",
    expected: "May 2026",
    coursework: [
      "Data Structures",
      "Algorithms and Design",
      "Software Engineering",
      "System Programming",
      "Computer Architecture",
      "Server-Side Programming",
      "Discrete Information Structures",
      "Object-Oriented Programming"
    ]
  };

  const experiences = [
    {
      company: "Johnson Controls",
      position: "Software Engineering Intern",
      period: "May 2025 – Present",
      location: "Milwaukee, WI",
      bullets: [
        "Built code generators in Python and Java alongside REST API endpoints and TypeScript types, streamlining client integration and ensuring consistent API contracts",
        "Improved system reliability by developing unit, integration, and system tests alongside deployment workflows",
        "Coordinated development across multiple interconnected repositories while refactoring and modernizing legacy code, reducing technical debt and improving maintainability",
        "Owned CCT deployment workflows by maintaining CI/CD pipelines, publishing container images via Jenkins, and managing Azure App Service deployments"
      ]
    },
    {
      company: "International Travel Advisor (ITA)",
      position: "Software Engineering Intern",
      period: "December 2024 – April 2025",
      location: "Remote",
      bullets: [
        "Built a rule-based recommendation engine in Python/Django to match users with ideal international destinations, improving preference alignment across 9+ metrics",
        "Automated the end-to-end workflow with Zapier, Google Forms, and Mailchimp, reducing manual processing time by 70% and fixing API/data-mapping issues to cut email errors by 90%",
        "Delivered and authored a technical white paper detailing system design, logic, and future improvements"
      ]
    },
    {
      company: "Amazon x CodePath",
      position: "Software Engineering Fellowship",
      period: "June 2024 – August 2024",
      location: "Remote",
      bullets: [
        "Mastered advanced data structures and algorithms through Amazon x CodePath's 10-week TIP course",
        "Solved 50+ complex algorithm problems and strengthened technical skills through weekly exercises/virtual sessions"
      ]
    }
  ];

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = entry.target.dataset.index;
          setVisibleItems((prev) => new Set([...prev, index]));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Set initial visibility for all items
    const allIndices = new Set();
    for (let i = 0; i <= experiences.length; i++) {
      allIndices.add(i.toString());
    }
    setVisibleItems(allIndices);

    // Capture refs at the start of the effect
    const currentRefs = itemRefs.current;
    let timeoutId;

    // Observe items after a short delay to ensure they're rendered
    timeoutId = setTimeout(() => {
      currentRefs.forEach((ref, index) => {
        if (ref) {
          ref.dataset.index = index.toString();
          observer.observe(ref);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      currentRefs.forEach((ref) => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, [experiences.length]);

  return (
    <section id='experience'>
      <span className="experienceTitle">Experience</span>
      <span className="experienceDesc">My professional journey and academic background</span>
      
      <div className="experienceContainer">
        {/* Education Section */}
        <div className="timelineSection">
          <div 
            ref={(el) => (itemRefs.current[0] = el)}
            className={`timelineItem education ${visibleItems.has('0') ? 'visible' : ''}`}
          >
            <div className="timelineMarker educationMarker"></div>
            <div className="timelineContent">
              <div className="experienceHeader">
                <h3 className="companyName">{education.institution}</h3>
                <span className="period">Expected {education.expected}</span>
              </div>
              <p className="position">{education.degree}</p>
              <p className="location">GPA: {education.gpa} | {education.location}</p>
              <div className="coursework">
                <p className="courseworkTitle">Relevant Coursework:</p>
                <div className="courseworkTags">
                  {education.coursework.map((course, index) => (
                    <span key={index} className="courseTag">{course}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Experience Section */}
        <div className="timelineSection experienceSection">
          {experiences.map((exp, index) => (
            <div 
              key={index} 
              ref={(el) => (itemRefs.current[index + 1] = el)}
              className={`timelineItem ${visibleItems.has(String(index + 1)) ? 'visible' : ''}`}
            >
              <div className="timelineMarker"></div>
              <div className="timelineContent">
                <div className="experienceHeader">
                  <h3 className="companyName">{exp.company}</h3>
                  <span className="period">{exp.period}</span>
                </div>
                <p className="position">{exp.position}</p>
                <p className="location">{exp.location}</p>
                <ul className="experienceBullets">
                  {exp.bullets.map((bullet, bulletIndex) => (
                    <li key={bulletIndex}>{bullet}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
